import React, { useEffect, useState, useCallback } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, X } from "lucide-react";
import Layout from "@/components/Layout";
import BillingCard from "@/components/BillingCard";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { applyBrandKit } from "@/lib/brandKit";
import { replayTour } from "@/lib/onboarding";
import { fmtDate } from "@/lib/leadUtils";

/* ============================================================================
 * Phase 7.0 — Settings (Team / Reminders / Brand Kit) restyle.
 * Warm-ivory editorial system @ Catalog R1 / Accounts R2 / Import compact
 * density. VISUAL-ONLY: every data-testid, every native <select> / <input> /
 * <input type="file"> / <input type="checkbox">, every useState/useEffect/
 * useCallback, every axios call, and every toast invocation is preserved
 * bit-for-bit from R0. The Team Roster grid replaces the <table> with a CSS
 * grid keyed by data-testid="team-table" so the testid is preserved while the
 * presentation switches to compact ds-pill rows.
 * ========================================================================== */

const ROLES = [
  { value: "owner",       label: "Owner" },
  { value: "salesperson", label: "Salesperson" },
  { value: "pm",          label: "Production Manager" },
  { value: "dm",          label: "Delivery Manager" },
  { value: "accounts",    label: "Accounts Manager" },
];

const TEAM_GRID = "minmax(0,1.6fr) minmax(0,1fr) 140px 70px 88px 28px";
const RULE_GRID = "minmax(0,1.6fr) minmax(0,1fr) 170px 130px";

function StatusBadge({ status }) {
  const invited = status === "invited";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "2px 5px",
        fontSize: 8,
        fontWeight: 700,
        letterSpacing: "0.16em",
        textTransform: "uppercase",
        background: invited ? "transparent" : "#E4EDDF",
        color: invited ? "#A39A8B" : "#3F6B51",
        border: invited ? "1px solid #E4DCCD" : "1px solid transparent",
        borderRadius: 4,
      }}
      data-testid={`team-status-${status}`}
      title={invited ? "Hasn't signed in yet" : "Has signed in"}
    >
      {invited ? "Invited" : "Active"}
    </span>
  );
}

function InviteModal({ open, onClose, onInvited }) {
  const [email, setEmail] = useState("");
  const [role, setRole]   = useState("pm");
  const [busy, setBusy]   = useState(false);
  if (!open) return null;
  const submit = async () => {
    if (!email.trim()) { toast.error("Email required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/team", { email: email.trim(), role });
      toast.success(`Invited ${data.member.email} as ${data.member.role}`);
      setEmail(""); setRole("pm");
      onInvited(data.member);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invite failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div
      data-testid="invite-modal"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 50,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
        background: "rgba(34,56,44,0.42)", backdropFilter: "blur(2px)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#FFFFFF",
          maxWidth: 440, width: "100%",
          border: "1px solid #E9E2D6",
          borderTop: "3px solid #22382C",
          borderRadius: 10,
          boxShadow: "0 18px 40px rgba(34,56,44,.18)",
          padding: 24,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
          <div>
            <div className="sx-eyebrow-gold" style={{ marginBottom: 4 }}>INVITE TEAMMATE</div>
            <div style={{ fontSize: 12, color: "#76705F", fontStyle: "italic", lineHeight: 1.5 }}>
              Enter the exact Google email they&apos;ll sign in with.
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="invite-close-btn"
            className="sx-icon-btn"
            style={{ marginLeft: 12 }}
          >
            <X size={16} />
          </button>
        </div>
        <SxField label="Email">
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="teammate@gmail.com"
            className="sx-input"
            data-testid="invite-email-input"
            type="email"
            autoFocus
          />
        </SxField>
        <div style={{ height: 12 }} />
        <SxField label="Role">
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="sx-input"
            data-testid="invite-role-select"
          >
            {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </SxField>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 22 }}>
          <button
            onClick={onClose} disabled={busy}
            className="sx-ghost-btn"
            data-testid="invite-cancel-btn"
          >
            Cancel
          </button>
          <button
            onClick={submit} disabled={busy}
            className="sx-primary-btn"
            data-testid="invite-submit-btn"
          >
            {busy ? "Sending…" : "Send invite"}
          </button>
        </div>
      </div>
    </div>
  );
}

function RemoveModal({ member, onCancel, onConfirm, busy }) {
  if (!member) return null;
  return (
    <div
      data-testid="remove-modal"
      onClick={onCancel}
      style={{
        position: "fixed", inset: 0, zIndex: 50,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
        background: "rgba(34,56,44,0.42)", backdropFilter: "blur(2px)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#FFFFFF",
          maxWidth: 440, width: "100%",
          border: "1px solid #E9E2D6",
          borderTop: "3px solid #A35C45",
          borderRadius: 10,
          boxShadow: "0 18px 40px rgba(34,56,44,.18)",
          padding: 24,
        }}
      >
        <div className="sx-eyebrow-gold" style={{ marginBottom: 6 }}>REMOVE TEAMMATE</div>
        <div className="font-display" style={{ fontSize: 22, color: "#22382C", marginBottom: 8, lineHeight: 1.15 }}>
          {member.name || member.email}
        </div>
        <div style={{ fontSize: 13, color: "#76705F", lineHeight: 1.55 }}>
          This revokes their access immediately. Any active sessions will be killed and they&apos;ll
          be bounced to the login screen on their next request.
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 22 }}>
          <button
            onClick={onCancel} disabled={busy}
            className="sx-ghost-btn"
            data-testid="remove-cancel-btn"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm} disabled={busy}
            data-testid="remove-confirm-btn"
            style={{
              padding: "8px 16px", minHeight: 34, fontSize: 12, fontWeight: 600,
              letterSpacing: "0.04em", color: "#FFFFFF",
              background: busy ? "#C7867A" : "#A35C45",
              border: `1px solid ${busy ? "#C7867A" : "#A35C45"}`,
              borderRadius: 7, cursor: busy ? "not-allowed" : "pointer",
            }}
          >
            {busy ? "Removing…" : "Remove access"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Settings() {
  const { user, can, loading: authLoading } = useAuth();
  const location = useLocation();
  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [inviteOpen, setInvite] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [busyRemove, setBusy]   = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/team");
      setItems(data.items || []);
    } catch (e) {
      if (e?.response?.status !== 403) {
        toast.error("Couldn't load team — " + (e?.response?.data?.detail || e.message));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && can("team_manage")) refresh();
  }, [refresh, authLoading, can]);

  // Module 6C.4 Layer 2.1 — hash-anchor scroll on first paint and hash change.
  // Enables /settings#billing (sidebar Upgrade pill, Dashboard banner CTAs).
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.replace("#", "");
    // Two rAFs: one to let React commit the DOM, one to let the ref settle.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    });
  }, [location.hash, loading]);

  if (authLoading) return null;
  if (user && !can("team_manage")) return <Navigate to="/dashboard" replace />;

  const onRoleChange = async (member, newRole) => {
    if (newRole === member.role) return;
    const prev = items;
    setItems((arr) => arr.map((m) => m.user_id === member.user_id ? { ...m, role: newRole } : m));
    try {
      await api.patch(`/team/${member.user_id}`, { role: newRole });
      toast.success(`Role updated to ${newRole}`);
      refresh();
    } catch (e) {
      setItems(prev);
      toast.error(e?.response?.data?.detail || "Role change failed");
    }
  };

  const onRemove = async () => {
    if (!removing) return;
    setBusy(true);
    try {
      await api.delete(`/team/${removing.user_id}`);
      toast.success(`Removed ${removing.email}`);
      setRemoving(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout>
      <div className="fade-up" data-testid="settings-page">
        {/* Page header */}
        <div
          style={{
            display: "flex", alignItems: "flex-end", justifyContent: "space-between",
            flexWrap: "wrap", gap: 16, marginBottom: 26,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 10, fontWeight: 700, letterSpacing: "0.18em",
                textTransform: "uppercase", color: "#B08D55", marginBottom: 8,
              }}
            >
              SETTINGS
            </div>
            <h1
              className="font-display settings-h1"
              style={{ fontSize: 44, lineHeight: 1.05, color: "#22382C", margin: 0 }}
            >
              Team
            </h1>
          </div>
          <button
            onClick={async () => {
              try {
                await replayTour();
                toast.success("Tour will replay on your next visit to Dashboard.");
              } catch {
                toast.error("Could not reset tour. Try again.");
              }
            }}
            data-testid="replay-tour-btn"
            className="sx-link-btn"
          >
            Replay tour
          </button>
        </div>

        {/* Module 6C.4 Layer 2.1 — scroll-anchor tab strip. Same-page
            anchors (not routes) so we don't split Settings across four
            pages. Clicking a tab scrolls to that card. */}
        <SettingsTabs />

        {/* Module 6C.4 Layer 2.1 — Billing & Plan (the primary
            conversion surface). Owner-gated by /settings route already. */}
        <BillingCard />

        {/* Anchor for the Team section (scroll target) */}
        <div id="team" style={{ scrollMarginTop: 20 }} />

        {/* Team Roster card */}
        <SxCard>
          <SxCardHeader
            title="TEAM ROSTER"
            subtitle="Invite by email — they sign in with Google using that exact address."
            action={
              <button
                onClick={() => setInvite(true)}
                className="sx-primary-btn"
                data-testid="invite-open-btn"
                style={{ display: "inline-flex", alignItems: "center", gap: 7 }}
              >
                <Plus size={13} /> Invite teammate
              </button>
            }
          />
          {loading ? (
            <div
              style={{ padding: "26px 18px", textAlign: "center", fontSize: 13, color: "#76705F" }}
              data-testid="team-loading"
            >
              Loading…
            </div>
          ) : items.length === 0 ? (
            <div
              style={{
                padding: "26px 18px", textAlign: "center", fontSize: 13,
                fontStyle: "italic", color: "#B0A891",
              }}
              data-testid="team-empty"
            >
              No teammates yet.
            </div>
          ) : (
            <div data-testid="team-table">
              {/* Column header */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: TEAM_GRID,
                  gap: 10,
                  padding: "0 6px",
                  marginBottom: 2,
                  fontSize: 9, fontWeight: 700, letterSpacing: "0.13em",
                  textTransform: "uppercase", color: "#A39A8B",
                }}
              >
                <span>Email</span>
                <span>Name</span>
                <span>Role</span>
                <span>Status</span>
                <span>Invited</span>
                <span />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {items.map((m) => (
                  <div
                    key={m.user_id}
                    data-testid={`team-row-${m.user_id}`}
                    style={{
                      display: "grid",
                      gridTemplateColumns: TEAM_GRID,
                      gap: 10,
                      alignItems: "center",
                      background: "#FFFFFF",
                      border: "1px solid #EDE5D8",
                      borderRadius: 6,
                      padding: "3px 6px",
                      transition: "box-shadow .15s ease, transform .15s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow = "0 4px 10px rgba(60,46,20,.07)";
                      e.currentTarget.style.transform = "translateY(-1px)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = "none";
                      e.currentTarget.style.transform = "translateY(0)";
                    }}
                  >
                    <span
                      style={{
                        fontSize: 11, color: "#2A2722",
                        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                      }}
                      data-testid={`team-row-email-${m.user_id}`}
                    >
                      {m.email}
                    </span>
                    <span style={{ fontSize: 11, fontWeight: 500, color: m.name ? "#2A2722" : "#A39A8B" }}>
                      {m.name || "—"}
                    </span>
                    <select
                      value={m.role}
                      onChange={(e) => onRoleChange(m, e.target.value)}
                      disabled={m.user_id === user?.user_id}
                      title={m.user_id === user?.user_id ? "You can't change your own role" : ""}
                      className="sx-select"
                      data-testid={`team-role-select-${m.user_id}`}
                    >
                      {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                    <StatusBadge status={m.status} />
                    <span style={{ fontSize: 9, color: "#A39A8B" }}>
                      {m.invited_at ? fmtDate(m.invited_at) : (m.activated_at ? fmtDate(m.activated_at) : "—")}
                    </span>
                    <span style={{ textAlign: "right" }}>
                      {m.user_id === user?.user_id ? (
                        <span
                          style={{
                            display: "inline-flex", alignItems: "center",
                            padding: "2px 5px",
                            fontSize: 8, fontWeight: 700, letterSpacing: "0.16em",
                            textTransform: "uppercase",
                            background: "#F4EDDF", color: "#9A7A3C",
                            borderRadius: 4,
                          }}
                        >
                          You
                        </span>
                      ) : (
                        <button
                          onClick={() => setRemoving(m)}
                          className="sx-icon-btn"
                          title="Remove teammate"
                          data-testid={`team-remove-btn-${m.user_id}`}
                          style={{ color: "#A39A8B" }}
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </SxCard>

        <InviteModal
          open={inviteOpen}
          onClose={() => setInvite(false)}
          onInvited={() => refresh()}
        />
        <RemoveModal
          member={removing}
          onCancel={() => setRemoving(null)}
          onConfirm={onRemove}
          busy={busyRemove}
        />

        <div id="reminders" style={{ scrollMarginTop: 20 }} />
        <OperationalRulesCard />
        <div id="brand" style={{ scrollMarginTop: 20 }} />
        <BrandKitCard />
      </div>

      {/* Page-scoped class library */}
      <style>{`
        .sx-eyebrow-gold{
          font-size:10px; font-weight:700; letter-spacing:0.13em;
          text-transform:uppercase; color:#B08D55;
        }
        .sx-link-btn{
          font-size:11px; font-weight:600; letter-spacing:0.10em;
          text-transform:uppercase; color:#76705F;
          background:transparent; border:none; cursor:pointer;
          padding:6px 8px; min-height:24px;
          text-decoration:underline; text-underline-offset:4px;
          transition:color .15s;
        }
        .sx-link-btn:hover{color:#22382C;}
        .sx-primary-btn{
          padding:8px 16px; min-height:34px;
          font-size:12px; font-weight:600; letter-spacing:0.04em;
          background:#22382C; color:#F4EFE4;
          border:1px solid #22382C; border-radius:7px;
          cursor:pointer; transition:background .15s, border-color .15s;
        }
        .sx-primary-btn:hover:not(:disabled){background:#2C4A3B; border-color:#2C4A3B;}
        .sx-primary-btn:disabled{opacity:0.6; cursor:not-allowed;}
        .sx-ghost-btn{
          padding:8px 14px; min-height:34px;
          font-size:12px; font-weight:600; letter-spacing:0.04em;
          background:#FFFFFF; color:#2A2722;
          border:1px solid #E4DCCD; border-radius:7px;
          cursor:pointer; transition:border-color .15s, color .15s, background .15s;
        }
        .sx-ghost-btn:hover:not(:disabled){
          border-color:#22382C; color:#22382C; background:#FAF6EC;
        }
        .sx-ghost-btn:disabled{opacity:0.6; cursor:not-allowed;}
        .sx-icon-btn{
          display:inline-flex; align-items:center; justify-content:center;
          min-width:26px; min-height:26px; padding:5px;
          background:transparent; color:#76705F;
          border:1px solid transparent; border-radius:6px;
          cursor:pointer; transition:color .15s, border-color .15s, background .15s;
        }
        .sx-icon-btn:hover{color:#22382C; border-color:#E4DCCD; background:#FAF6EC;}
        .sx-input{
          width:100%; height:36px; padding:0 10px;
          background:#FFFFFF; border:1px solid #E4DCCD; border-radius:7px;
          font-size:13px; color:#2A2722; outline:none;
          font-family:var(--ds-body-font);
          transition:border-color .15s;
        }
        .sx-input:focus{border-color:#22382C;}
        .sx-select{
          width:100%; min-height:24px; padding:4px 6px;
          background:#FCFAF5; border:1px solid #E4DCCD; border-radius:6px;
          font-size:11px; color:#2A2722; outline:none;
          font-family:var(--ds-body-font);
          transition:border-color .15s, background .15s;
          cursor:pointer;
        }
        .sx-select:focus{border-color:#22382C; background:#FFFFFF;}
        .sx-select:disabled{background:#F2EDE2; color:#A39A8B; cursor:not-allowed;}
        .sx-num-input{
          width:42px; height:24px; padding:0 6px;
          background:#FCFAF5; border:1px solid #E4DCCD; border-radius:6px;
          font-size:11px; color:#2A2722; outline:none;
          font-family:var(--ds-body-font);
          transition:border-color .15s, background .15s;
          text-align:center;
        }
        .sx-num-input:focus{border-color:#22382C; background:#FFFFFF;}

        /* Phase 7.1 Pass 5 — mobile visual layout (< 1024px). Zero JSX/logic
           changes. Converts the dense desktop grids to stacked cards, shrinks
           the display heading, and makes the horizontally-scrolling brand-kit
           swatch row hide its scrollbar. Every data-testid remains unchanged. */
        @media (max-width: 1023px) {
          .settings-h1 { font-size: 34px !important; font-weight: 500 !important; }
          /* Team roster rows: hide the desktop column-header row, stack each
             member row into a card so email/name/role/status/actions are
             each on their own line. */
          [data-testid="team-table"] > div:first-child { display: none !important; }
          [data-testid^="team-row-"] {
            display: flex !important;
            flex-direction: column !important;
            align-items: stretch !important;
            gap: 8px !important;
            padding: 14px 15px !important;
            border-radius: 14px !important;
            border-color: rgba(34,56,44,0.10) !important;
            background: #FFFDFA !important;
          }
          [data-testid^="team-row-"] > * { min-width: 0; }
          [data-testid^="team-row-"] .sx-select { min-height: 44px !important; padding: 8px 12px !important; font-size: 13px !important; }
          /* Operational reminders rows: same stack-and-relax pattern */
          .ops-col-header { display: none !important; }
          [data-testid^="ops-rule-row-"] {
            display: flex !important;
            flex-direction: column !important;
            grid-template-columns: none !important;
            gap: 8px !important;
            padding: 14px 15px !important;
            border-radius: 14px !important;
            background: #FFFDFA !important;
          }
          [data-testid^="ops-rule-row-"] > * { min-width: 0; }
          [data-testid^="ops-rule-row-"] select,
          [data-testid^="ops-rule-row-"] input[type="number"] { min-height: 44px !important; font-size: 13px !important; }
          /* Brand-kit swatches: hide scrollbar cosmetically. And relax the
             minWidth:220 file-input column so 320px viewports don't hscroll. */
          [data-testid="brand-kit-card"] .sx-hscroll { scrollbar-width: none; }
          [data-testid="brand-kit-card"] .sx-hscroll::-webkit-scrollbar { display: none; }
          .bk-logo-input-col { min-width: 0 !important; flex-basis: 100% !important; }
          .bk-typography-grid { grid-template-columns: 1fr !important; }
          .bk-typography-grid > button { min-width: 0 !important; overflow: hidden; }
          .bk-typography-grid > button > div { overflow: hidden; text-overflow: ellipsis; }
        }
      `}</style>
    </Layout>
  );
}

// ============================================================================
// Module 6C.4 Layer 2.1 — Scroll-anchor tab strip
// ============================================================================
const TAB_ITEMS = [
  { id: "billing",   label: "Billing & Plan" },
  { id: "team",      label: "Team" },
  { id: "reminders", label: "Reminders" },
  { id: "brand",     label: "Brand Kit" },
];

function SettingsTabs() {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      // Update the hash without triggering a full navigation.
      history.replaceState(null, "", `#${id}`);
    }
  };
  return (
    <div
      data-testid="settings-tabs"
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 4,
        marginBottom: 20,
        padding: 4,
        background: "#F4EFE4",
        border: "1px solid #E9E2D6",
        borderRadius: 8,
      }}
    >
      {TAB_ITEMS.map((t) => (
        <button
          key={t.id}
          onClick={() => scrollTo(t.id)}
          data-testid={`settings-tab-${t.id}`}
          style={{
            padding: "8px 14px",
            fontSize: 11, fontWeight: 600, letterSpacing: "0.10em",
            textTransform: "uppercase",
            background: "transparent",
            color: "#22382C",
            border: "none",
            borderRadius: 6,
            cursor: "pointer",
            transition: "background .15s, color .15s",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "#FFFFFF"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}


function SxCard({ children }) {
  return (
    <div
      style={{
        background: "#FFFFFF",
        border: "1px solid #E9E2D6",
        borderRadius: 10,
        padding: "18px 22px",
        marginBottom: 20,
        boxShadow: "0 2px 6px rgba(60,46,20,.04)",
      }}
    >
      {children}
    </div>
  );
}

function SxCardHeader({ title, subtitle, action }) {
  return (
    <div
      style={{
        display: "flex", alignItems: "flex-start", justifyContent: "space-between",
        gap: 16, marginBottom: 14, flexWrap: "wrap",
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="sx-eyebrow-gold" style={{ marginBottom: 5 }}>{title}</div>
        <div style={{ fontSize: 13, color: "#76705F", lineHeight: 1.45 }}>
          {subtitle}
        </div>
      </div>
      {action && <div>{action}</div>}
    </div>
  );
}

function SxField({ label, children }) {
  return (
    <label style={{ display: "block" }}>
      <div
        style={{
          fontSize: 10, fontWeight: 700, letterSpacing: "0.18em",
          textTransform: "uppercase", color: "#A39A8B", marginBottom: 4,
        }}
      >
        {label}
      </div>
      {children}
    </label>
  );
}

// ============================================================================
// Module 6B — Operational reminders card
// ============================================================================
const ASSIGNEE_OPTIONS = [
  { value: "pm",          label: "Production Manager" },
  { value: "dm",          label: "Delivery Manager" },
  { value: "accounts",    label: "Accounts Manager" },
  { value: "salesperson", label: "Salesperson" },
  { value: "lead_owner",  label: "Lead owner (→ PM if unassigned)" },
];

function OperationalRulesCard() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [draft, setDraft] = useState({});

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/settings/operational-rules");
      setRules(data.rules || []);
    } catch (e) {
      toast.error("Couldn't load rules — " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const patchRule = async (id, patch) => {
    setBusy(id);
    const prev = rules;
    setRules((rs) => rs.map((r) => r.id === id ? { ...r, ...patch } : r));
    try {
      const { data } = await api.patch("/settings/operational-rules", {
        rules: [{ id, ...patch }],
      });
      setRules(data.rules || []);
      toast.success("Rule updated");
      setDraft((d) => { const c = { ...d }; delete c[id]; return c; });
    } catch (e) {
      setRules(prev);
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(null);
    }
  };

  const onToggle = (r) => patchRule(r.id, { enabled: !r.enabled });

  const onSaveDays = (r) => {
    const v = draft[r.id];
    if (v === undefined || v === "" || v === null) return;
    const days = parseInt(v, 10);
    if (Number.isNaN(days) || days < 0) {
      toast.error("Must be a non-negative number");
      return;
    }
    patchRule(r.id, { days_before_event: days });
  };

  const onAssignee = (r, role) => patchRule(r.id, { assignee_role: role });

  if (loading) {
    return (
      <div
        data-testid="ops-rules-loading"
        style={{
          background: "#FFFFFF", border: "1px solid #E9E2D6", borderRadius: 10,
          padding: "18px 22px", marginBottom: 20, boxShadow: "0 2px 6px rgba(60,46,20,.04)",
        }}
      >
        <div style={{ fontSize: 13, color: "#76705F" }}>Loading rules…</div>
      </div>
    );
  }

  return (
    <SxCardWithTestId testId="ops-rules-card">
      <SxCardHeader
        title="OPERATIONAL REMINDERS"
        subtitle="Live rules that compute tasks on the Follow-ups page. Tasks are never stored — they appear when their condition holds and vanish when it clears."
      />

      <div
        className="ops-col-header"
        style={{
          display: "grid",
          gridTemplateColumns: RULE_GRID,
          gap: 10,
          padding: "0 8px",
          marginBottom: 2,
          fontSize: 9, fontWeight: 700, letterSpacing: "0.13em",
          textTransform: "uppercase", color: "#A39A8B",
        }}
      >
        <span>Rule</span>
        <span>Trigger</span>
        <span>Assigned to</span>
        <span style={{ textAlign: "right" }}>Enabled</span>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {rules.map((r) => {
          const localDays = draft[r.id] !== undefined ? draft[r.id] : (r.days_before_event ?? "");
          const supportsDays = r.id !== "balance_overdue" && r.id !== "ready_to_close" && r.id !== "stale_lead";
          return (
            <div
              key={r.id}
              data-testid={`ops-rule-row-${r.id}`}
              style={{
                display: "grid",
                gridTemplateColumns: RULE_GRID,
                gap: 10,
                alignItems: "center",
                background: "#FFFFFF",
                border: "1px solid #EDE5D8",
                borderRadius: 6,
                padding: "4px 8px",
                boxShadow: "0 2px 6px rgba(60,46,20,.05)",
                transition: "box-shadow .15s ease, transform .15s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = "0 5px 14px rgba(60,46,20,.10)";
                e.currentTarget.style.transform = "translateY(-1px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = "0 2px 6px rgba(60,46,20,.05)";
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div
                  data-testid={`ops-rule-label-${r.id}`}
                  style={{ fontSize: 11, fontWeight: 600, color: "#22382C", lineHeight: 1.25 }}
                >
                  {r.label}
                </div>
                <div
                  style={{
                    fontSize: 9, color: "#9A9387",
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                    marginTop: 1,
                  }}
                >
                  {r.id}
                </div>
              </div>

              <div>
                {supportsDays ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <input
                      type="number"
                      min="0"
                      value={localDays}
                      onChange={(e) => setDraft((d) => ({ ...d, [r.id]: e.target.value }))}
                      onBlur={() => {
                        if (String(draft[r.id]) !== String(r.days_before_event ?? "") && draft[r.id] !== undefined) {
                          onSaveDays(r);
                        }
                      }}
                      className="sx-num-input"
                      data-testid={`ops-rule-days-${r.id}`}
                    />
                    <span style={{ fontSize: 10, color: "#76705F" }}>days before event</span>
                  </div>
                ) : (
                  <span style={{ fontSize: 10, fontStyle: "italic", color: "#B0A891" }}>
                    condition-based
                  </span>
                )}
              </div>

              <div>
                <select
                  value={r.assignee_role}
                  onChange={(e) => onAssignee(r, e.target.value)}
                  className="sx-select"
                  data-testid={`ops-rule-assignee-${r.id}`}
                >
                  {ASSIGNEE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>

              <div
                style={{
                  display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8,
                }}
              >
                <span
                  style={{
                    fontSize: 9, fontWeight: 600, letterSpacing: "0.16em",
                    textTransform: "uppercase",
                    color: r.enabled ? "#3F6B51" : "#A39A8B",
                  }}
                >
                  {r.enabled ? "ENABLED" : "DISABLED"}
                </span>
                <SxToggle
                  on={r.enabled}
                  onClick={() => onToggle(r)}
                  disabled={busy === r.id}
                  testId={`ops-rule-toggle-${r.id}`}
                  ariaLabel={r.enabled ? "Disable rule" : "Enable rule"}
                />
              </div>
            </div>
          );
        })}
      </div>
    </SxCardWithTestId>
  );
}

function SxCardWithTestId({ testId, children }) {
  return (
    <div
      data-testid={testId}
      style={{
        background: "#FFFFFF",
        border: "1px solid #E9E2D6",
        borderRadius: 10,
        padding: "18px 22px",
        marginBottom: 20,
        boxShadow: "0 2px 6px rgba(60,46,20,.04)",
      }}
    >
      {children}
    </div>
  );
}

function SxToggle({ on, onClick, disabled, testId, ariaLabel }) {
  // 36×20 visible track, but the <button> wrapper is min 24px tall to keep
  // the WCAG 2.5.5 tappable area healthy.
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      aria-label={ariaLabel}
      style={{
        position: "relative",
        display: "inline-flex", alignItems: "center", justifyContent: "flex-start",
        width: 30, height: 24,
        padding: 0,
        background: "transparent", border: "none",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <span
        style={{
          position: "absolute", left: 0, right: 0, top: "50%",
          transform: "translateY(-50%)",
          height: 17, width: 30,
          background: on ? "#22382C" : "#E4DCCD",
          borderRadius: 999,
          transition: "background .15s ease",
        }}
      />
      <span
        style={{
          position: "absolute",
          top: "50%",
          transform: `translateY(-50%) translateX(${on ? 15 : 2}px)`,
          width: 13, height: 13,
          background: "#FFFFFF",
          borderRadius: 999,
          boxShadow: "0 1px 2px rgba(0,0,0,.18)",
          transition: "transform .18s ease",
        }}
      />
    </button>
  );
}

// ============================================================================
// Module 6C.2 — Brand Kit card
// ============================================================================
function BrandKitCard() {
  const { studio, setStudio } = useAuth();
  const [presets, setPresets] = useState({ colors: [], typography: [] });
  const [customHex, setCustomHex] = useState("");
  const [busy, setBusy] = useState(false);
  const [logoVersion, setLogoVersion] = useState(0);

  useEffect(() => { api.get("/brand-kit/presets").then(r => setPresets(r.data)); }, []);

  const onPickColor = async (value) => {
    setBusy(true);
    try {
      const { data } = await api.patch("/settings/brand-kit", { primary_color: value });
      setStudio(data.studio);
      applyBrandKit(data.studio);
      toast.success("Color saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const onPickTypography = async (preset) => {
    setBusy(true);
    try {
      const { data } = await api.patch("/settings/brand-kit", { typography_preset: preset });
      setStudio(data.studio);
      applyBrandKit(data.studio);
      toast.success("Typography saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const onLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) { toast.error("Logo must be ≤ 500KB"); return; }
    setBusy(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/settings/brand-kit/logo", form,
        { headers: { "Content-Type": "multipart/form-data" } });
      setStudio({ ...studio, logo_mime: data.logo_mime, logo_updated_at: data.logo_updated_at });
      setLogoVersion(v => v + 1);
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  const onLogoDelete = async () => {
    setBusy(true);
    try {
      await api.delete("/settings/brand-kit/logo");
      setStudio({ ...studio, logo_mime: null, logo_updated_at: null });
      setLogoVersion(v => v + 1);
      toast.success("Logo removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove failed");
    } finally { setBusy(false); }
  };

  const liveLogoUrl = studio?.logo_updated_at
    ? `${process.env.REACT_APP_BACKEND_URL}/api/studios/${studio.id}/logo?v=${logoVersion}`
    : null;

  return (
    <SxCardWithTestId testId="brand-kit-card">
      <SxCardHeader
        title="BRAND KIT"
        subtitle="Logo, primary color, and typography for your studio."
      />

      {/* Logo */}
      <div data-testid="bk-logo-section" style={{ marginBottom: 18 }}>
        <SubsectionLabel>Logo</SubsectionLabel>
        <div style={{ display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap" }}>
          <div
            data-testid="bk-logo-preview"
            style={{
              width: 84, height: 84,
              border: "1px solid #E4DCCD", borderRadius: 8,
              background: "#FAF6EC",
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {liveLogoUrl ? (
              <img src={liveLogoUrl} alt="Logo" style={{ maxWidth: "85%", maxHeight: "85%" }} />
            ) : (
              <span style={{ fontSize: 11, fontStyle: "italic", color: "#A39A8B" }}>No logo</span>
            )}
          </div>
          <div className="bk-logo-input-col" style={{ flex: 1, minWidth: 220 }}>
            <input
              type="file"
              accept="image/png,image/svg+xml"
              onChange={onLogoUpload}
              disabled={busy}
              data-testid="bk-logo-input"
              style={{ display: "block", fontSize: 12, color: "#2A2722" }}
            />
            <div style={{ fontSize: 11, color: "#A39A8B", marginTop: 6 }}>
              PNG (≥256×256) or SVG. Max 500KB.
            </div>
          </div>
          {liveLogoUrl && (
            <button
              onClick={onLogoDelete}
              disabled={busy}
              className="sx-ghost-btn"
              data-testid="bk-logo-delete"
            >
              Remove
            </button>
          )}
        </div>
      </div>

      <Divider />

      {/* Primary color */}
      <div data-testid="bk-color-section" style={{ marginBottom: 18 }}>
        <SubsectionLabel>Primary color</SubsectionLabel>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 14 }}>
          {presets.colors.map(c => {
            const active = (studio?.primary_color || "").toLowerCase() === c.hex.toLowerCase();
            return (
              <button
                key={c.id}
                onClick={() => onPickColor(c.id)}
                disabled={busy}
                data-testid={`bk-color-${c.id}`}
                title={c.label}
                style={{
                  display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
                  background: "transparent", border: "none", cursor: busy ? "not-allowed" : "pointer",
                  padding: 2, minHeight: 24,
                }}
              >
                <span
                  style={{
                    display: "block", width: 38, height: 38,
                    background: c.hex,
                    border: active ? "2px solid #22382C" : "2px solid #EDE5D8",
                    boxShadow: active ? "0 2px 6px rgba(60,46,20,.18)" : "none",
                    borderRadius: 8,
                    transition: "border-color .15s, box-shadow .15s",
                  }}
                />
                <span
                  style={{
                    fontSize: 9, fontWeight: 600, letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    color: active ? "#22382C" : "#A39A8B",
                  }}
                >
                  {c.label}
                </span>
              </button>
            );
          })}
          <div
            style={{
              display: "flex", alignItems: "center", gap: 8,
              marginLeft: 8, paddingLeft: 14,
              borderLeft: "1px solid #EDE5D8",
              minHeight: 38,
            }}
          >
            <span style={{ fontSize: 11, color: "#76705F" }}>Custom:</span>
            <input
              type="text"
              value={customHex}
              placeholder="#abcdef"
              onChange={e => setCustomHex(e.target.value)}
              data-testid="bk-custom-hex"
              style={{
                width: 96, height: 30, padding: "0 8px",
                background: "#FFFFFF", border: "1px solid #E4DCCD",
                borderRadius: 7,
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                fontSize: 12, color: "#2A2722", outline: "none",
              }}
            />
            <button
              onClick={() => customHex && onPickColor(customHex)}
              disabled={busy || !customHex}
              data-testid="bk-custom-hex-apply"
              className="sx-ghost-btn"
              style={{ padding: "5px 10px", minHeight: 30, fontSize: 11 }}
            >
              Apply
            </button>
          </div>
        </div>
      </div>

      <Divider />

      {/* Typography */}
      <div data-testid="bk-typography-section">
        <SubsectionLabel>Typography</SubsectionLabel>
        <div
          className="bk-typography-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 12,
          }}
        >
          {presets.typography.map(t => {
            const active = (studio?.typography_preset || "editorial") === t.id;
            return (
              <button
                key={t.id}
                onClick={() => onPickTypography(t.id)}
                disabled={busy}
                data-testid={`bk-typography-${t.id}`}
                style={{
                  position: "relative",
                  textAlign: "left",
                  background: "#FFFFFF",
                  border: active ? "1px solid #E9E2D6" : "1px solid #E9E2D6",
                  borderTop: active ? "3px solid #22382C" : "3px solid transparent",
                  borderRadius: 10,
                  padding: "14px 16px",
                  cursor: busy ? "not-allowed" : "pointer",
                  boxShadow: active ? "0 4px 10px rgba(60,46,20,.10)" : "0 2px 6px rgba(60,46,20,.05)",
                  transition: "box-shadow .15s ease, transform .15s ease",
                  minHeight: 96,
                }}
                onMouseEnter={(e) => {
                  if (busy) return;
                  e.currentTarget.style.boxShadow = "0 5px 14px rgba(60,46,20,.10)";
                  e.currentTarget.style.transform = "translateY(-1px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = active ? "0 4px 10px rgba(60,46,20,.10)" : "0 2px 6px rgba(60,46,20,.05)";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                <div
                  style={{
                    fontSize: 9, fontWeight: 700, letterSpacing: "0.14em",
                    textTransform: "uppercase", color: "#B08D55",
                    marginBottom: 6,
                  }}
                >
                  {t.label}
                </div>
                <div
                  style={{
                    fontSize: 20, color: "#22382C", lineHeight: 1.15,
                    fontFamily: `'${t.display}', serif`,
                  }}
                >
                  {t.display}
                </div>
                <div
                  style={{
                    fontSize: 12, marginTop: 4, color: "#76705F",
                    fontFamily: `'${t.body}', sans-serif`,
                  }}
                >
                  {t.body} body sample
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </SxCardWithTestId>
  );
}

function SubsectionLabel({ children }) {
  return (
    <div
      style={{
        fontSize: 10, fontWeight: 700, letterSpacing: "0.18em",
        textTransform: "uppercase", color: "#A39A8B", marginBottom: 10,
      }}
    >
      {children}
    </div>
  );
}

function Divider() {
  return <div style={{ height: 1, background: "#F2EDE2", marginBottom: 18 }} />;
}
